import React from 'react';
import { Tooltip, Drawer, Switch, Theme, useTheme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridValueFormatterParams
} from '@mui/x-data-grid';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { ExternalCompany, Scope } from '@esg/esg-global-types';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import moment, { Moment } from 'moment';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import { DocumentReference } from 'firebase/firestore';
import {
  createExternalCompany,
  deleteExternalCompany,
  external_company_label,
  getExternalCompanies,
  updateExternalCompany
} from '../../../lib/app/external_company';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import ScopeSelect from '../../shared/input/select/ScopeSelect';
import { ExternalCompanyData } from '../../../@types/shared';
import { useSubscriptionContext } from '../../../context/SubscriptionContext';
import { ExceededQuotaKey } from '../../../lib/app/subscription';

const PanelExternalCompanies = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = React.useState(false);
  const [isCreateWidget, setIsCreateWidget] = React.useState(true);
  const [gridLoading, setGridLoading] = React.useState(true);
  const [externalCompanyRows, setExternalCompanyRows] = React.useState<Array<ExternalCompany>>([]);
  const [actionRow, setActionRow] = React.useState<ExternalCompanyData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const { incrementSubscriptionUsage, decrementSubscriptionUsage, getExceededSubscriptionKeys } =
    useSubscriptionContext();

  const exceeded_subscription_keys: Array<ExceededQuotaKey> = getExceededSubscriptionKeys([
    'external_companies'
  ]);

  // Component variables
  const grid_row_format_timestamp = 'DD MMM yyyy hh:mm';
  const entity_name = (is_plural = false) => {
    is_plural ? 'External Companies' : 'External Company';
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setDisplayWidgetPanelRight(true);
    setIsCreateWidget(true);
  };

  const handleEditClick = (row: ExternalCompany): void => {
    setActionRow(row);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleDeleteClick = (row: ExternalCompany): void => {
    setActionRow(row);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
  };

  const scopeInputFactory = (
    handleInputChange: (input_id: string, value: Scope | Scope[] | null) => void
  ): JSX.Element => {
    return (
      <ScopeSelect
        scope_prefix="3"
        selected_options={actionRow?.scope}
        handleChangeSelection={(scope: Scope | Scope[] | null) => {
          handleInputChange('scope', scope);
        }}
      />
    );
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      if (group && company) {
        // Load rows from database.
        let rows_loaded: Array<ExternalCompany> = await getExternalCompanies(group.id, company.id);
        // Load rows to memory.
        rows_loaded = rows_loaded.map((external_company: ExternalCompany) => {
          return {
            ...external_company,
            active: !external_company.active ? false : true,
            created: moment(external_company.created),
            modified: external_company.modified
              ? moment(external_company.modified)
              : moment(external_company.created)
          };
        });
        setExternalCompanyRows(rows_loaded);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while calling fetchRows.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to load ${String(entity_name(true)).toLowerCase()}. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  const deleteRow = async (row: ExternalCompany): Promise<void> => {
    try {
      if (group && company) {
        // Soft delete row from database.
        await deleteExternalCompany(group.id, company.id, row);
        // Delete row from memory.
        setExternalCompanyRows(
          externalCompanyRows.filter((obj: ExternalCompany) => {
            return obj.id != row.id;
          })
        );
        decrementSubscriptionUsage(['external_companies']);
      }
      // Close delete modal.
      setShowDeleteModal(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while calling deleteRow.',
          {
            group: group,
            company: company,
            row: row,
            externalCompanyRows: externalCompanyRows
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete ${String(entity_name()).toLowerCase()}. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const createRow = async (row: {
    active: boolean;
    name: string;
    scope: Scope;
    modified: Moment;
    created: Moment;
    deleted: null;
  }): Promise<void> => {
    try {
      if (group && company) {
        // Create row in database.
        const external_company_doc: DocumentReference = await createExternalCompany(
          group.id,
          company.id,
          {
            name: row.name,
            scope: {
              id: row.scope.id,
              scope: row.scope.scope,
              description: row.scope.description
            }
          }
        );
        if (external_company_doc) {
          // Create grid row in memory
          setExternalCompanyRows([
            {
              id: external_company_doc.id,
              ...row
            },
            ...externalCompanyRows
          ]);

          incrementSubscriptionUsage(['external_companies']);
        } else {
          throw new MetadataError('Could not create External Company in database', {
            external_company_doc: external_company_doc
          });
        }
      }
      // Close side menu.
      setDisplayWidgetPanelRight(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while calling createRow.',
          {
            group: group,
            company: company,
            external_company: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create external company. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (
    new_row_data: ExternalCompany,
    old_row_data: ExternalCompany
  ): Promise<void> => {
    try {
      if (group && company) {
        // Update row in database.
        await updateExternalCompany(group.id, company.id, new_row_data, old_row_data);
        // Update row in memory.
        setExternalCompanyRows(
          externalCompanyRows.map((external_company: ExternalCompany) => {
            return external_company.id === new_row_data.id
              ? { ...new_row_data, modified: moment() }
              : external_company;
          })
        );
      }
      setDisplayWidgetPanelRight(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while calling updateRow.',
          {
            group: group,
            company: company,
            new_row_data: new_row_data,
            externalCompanyRows: externalCompanyRows,
            old_row_data: old_row_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update external company. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        setExternalCompanyRows([]);
        fetchRows();
      })().catch((err: unknown) => {
        throw new Error(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while calling fetchRows.'
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelExternalCompanies failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [group, company]);

  const columns: Array<GridColDef> = [
    {
      field: 'active',
      headerName: 'Active',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      maxWidth: 90,
      renderCell: (params) => {
        return <Switch checked={params.row.active ? true : false} disabled />;
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'created',
      headerName: 'Created',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value == null) return '';
        return params.value.format(grid_row_format_timestamp);
      }
    },
    {
      field: 'modified',
      headerName: 'Modified',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams<Moment>) => {
        if (params.value == null) return '';
        return params.value.format(grid_row_format_timestamp);
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit external company">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={!row.active}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete external company">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_widget_inputs: readonly CreateInput[] = [
    {
      id: 'name',
      type: 'text',
      label: 'Name'
    },
    {
      id: 'scope',
      type: 'node',
      label: 'Scope',
      inputNodeFactory: scopeInputFactory
    }
  ];

  const edit_widget_inputs: Array<EditInput> = [
    {
      id: 'name',
      type: 'text',
      label: 'Name'
    },
    {
      id: 'scope',
      type: 'node',
      label: 'Scope',
      inputNodeFactory: scopeInputFactory
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={showDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
        allow_delete={true}
      />

      {/* Side Widget Panel */}
      <React.Fragment key={'right_add'}>
        <Drawer
          anchor={'right'}
          open={displayWidgetPanelRight}
          onClose={() => {
            setDisplayWidgetPanelRight(false);
          }}
          PaperProps={{ style: { width: '40%', padding: '1.5rem' } }}
        >
          {/* Create / Edit Widget */}
          {isCreateWidget ? (
            <ConfigAddWidget
              widget_label={external_company_label.one}
              icon={<CreateIcon height={40} fill={primary_blue} stroke={primary_blue} />}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              inputs={create_widget_inputs}
              createFunction={(name: string, scope: Scope) => {
                createRow({
                  active: true,
                  name: name,
                  scope: scope,
                  modified: moment(),
                  created: moment(),
                  deleted: null
                });
              }}
            />
          ) : (
            <ConfigEditWidget
              widget_label={external_company_label.one}
              icon={<EditIcon width={40} fill={theme.palette.primary.main} />}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              edit_entity={actionRow}
              inputs={edit_widget_inputs}
              handleEditInput={(input_id: string, value: unknown) =>
                actionRow && setActionRow({ ...actionRow, [input_id]: value })
              }
              confirmEditFunction={(
                updated_company: ExternalCompany,
                old_row_data: ExternalCompany
              ) => updateRow(updated_company, old_row_data)}
            />
          )}
        </Drawer>
      </React.Fragment>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={externalCompanyRows.length > 1 ? false : true}
        columnHeaderHeight={externalCompanyRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={externalCompanyRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={external_company_label}
              handleCreate={handleCreateClick}
              disable_create={!!exceeded_subscription_keys.length}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={external_company_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' } }}
      />
    </>
  );
};

export default PanelExternalCompanies;
